import React, { useState, useRef, useEffect } from 'react';
import { Menu, User, Search, Download, ChevronDown, Check, Users } from 'lucide-react';
import Spinner from './Spinner';
import { formatMinutes, formatDecimalToHours } from '../utils/timeFormat';

const PersonalStats = ({ setIsSidebarOpen }) => {
    // --- 상태 관리 ---
    const SERVER_BASE = process.env.REACT_APP_SERVER_BASE_URL;
    const [employees, setEmployees] = useState([]); // 빈 배열로 초기화
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const dropdownRef = useRef(null);

    const [stats, setStats] = useState([]);

    // API 호출 로직
    useEffect(() => {
        const fetchEmployees = async () => {
            setIsLoading(true);
            try {
                // 위에서 만든 사원 조회 API 호출
                const token = localStorage.getItem('token');
                const response = await fetch(`${SERVER_BASE}/stats/employees`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                ); 
                
                const result = await response.json();                
                if (result.success) {
                    // API 데이터 구조에 맞춰 매핑 (예: name, team, id 사용)
                    setEmployees(result.data);
                }
            } catch (error) {
                console.error("사원 데이터 로딩 실패:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchEmployees();
    }, []);

    useEffect(() => {
        if (!selectedEmployee) return;

        const fetchStats = async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('token');
                // 날짜 범위는 필요에 따라 state로 관리하거나 임의 설정
                const response = await fetch(
                    `${SERVER_BASE}/stats/individual-monthly?id=${selectedEmployee.id}&startDate=2024-01-01&endDate=2026-12-31`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                const result = await response.json();
                if (result.success) setStats(result.data);
            } catch (error) {
                console.error("통계 조회 실패:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchStats();
    }, [selectedEmployee]);

    // 검색 필터 로직 (API에서 불러온 데이터를 필터링)
    const filteredEmployees = employees.filter(emp =>
        emp.name.includes(searchTerm) || emp.id.toString().includes(searchTerm)
    );

    return (
        <>
            <header className="mb-2 flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <button 
                            onClick={() => setIsSidebarOpen(true)}
                            className="p-2 bg-white rounded-lg shadow-sm border border-slate-200 md:hidden text-slate-600 shrink-0"
                        >
                            <Menu size={24} />
                        </button>
                        <div className="flex items-center gap-2">
                            <div className="p-2 bg-indigo-600 rounded-lg text-white shadow-lg shadow-indigo-100">
                                <Users size={20} />
                            </div>
                            <h2 className="text-xl md:text-2xl font-bold text-slate-800 tracking-tight">개인별 통계</h2>
                        </div>
                    </div>
                </div>
            </header>

            {/* 필터 및 검색 바 (반응형) */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                
                {/* 검색 기능이 포함된 커스텀 셀렉트 박스 */}
                <div className="relative flex-1" ref={dropdownRef}>
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="w-full flex items-center justify-between px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    >
                        <div className="flex items-center gap-2 overflow-hidden">
                            <Users size={18} className="text-slate-400 shrink-0" />
                            <span className={`truncate text-sm ${selectedEmployee ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>
                                {selectedEmployee ? `${selectedEmployee.name}(${selectedEmployee.id})` : "임직원 검색 및 선택"}
                            </span>
                        </div>
                        <ChevronDown size={18} className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* 드롭다운 레이어 */}
                    {isOpen && (
                        <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1">
                            {/* 드롭다운 내부 검색창 */}
                            <div className="p-2 border-b border-slate-100 bg-slate-50/50">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                    <input
                                        type="text"
                                        autoFocus
                                        placeholder="검색"
                                        className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* 검색 결과 리스트 */}
                            <ul className="max-h-[200px] overflow-y-auto p-1">
                                {filteredEmployees.length > 0 ? (
                                    filteredEmployees.map((emp) => (
                                        <li
                                            key={emp.id}
                                            onClick={() => {
                                                setSelectedEmployee(emp);
                                                setIsOpen(false);
                                                setSearchTerm("");
                                            }}
                                            className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 rounded-lg cursor-pointer group"
                                        >
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-slate-700">{emp.name}</span>
                                                <span className="text-xs text-slate-400">{emp.joindate} · {emp.active} · {emp.team}</span>
                                            </div>
                                            {selectedEmployee?.id === emp.id && <Check size={16} className="text-blue-500" />}
                                        </li>
                                    ))
                                ) : (
                                    <li className="p-4 text-center text-sm text-slate-400">검색 결과가 없습니다.</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>

                {/* 엑셀 저장 버튼 (모바일에서는 가로 꽉 차게) */}
                {/* <button className="flex items-center justify-center gap-2 px-5 py-2 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-colors text-sm font-medium shrink-0">
                    <Download size={18} /> 
                    <span>엑셀 저장</span>
                </button> */}
            </div>

            {/* 통계 테이블 (데스크톱용) */}
            <div className="hidden md:block">
                {!isLoading && stats.length > 0 ? (
                    <>
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-x-auto">
                            <table className="w-full text-left border-collapse min-w-[800px]">
                                <thead className="bg-slate-50">
                                <tr>
                                    <th className="p-4 text-xs font-semibold text-slate-600">년월</th>
                                    <th className="p-4 text-xs font-semibold text-slate-600">주간평균 근무</th>
                                    <th className="p-4 text-xs font-semibold text-slate-600">야간 근무</th>
                                    <th className="p-4 text-xs font-semibold text-slate-600">야간 근무 횟수</th>
                                    <th className="p-4 text-xs font-semibold text-slate-600">휴일 근무</th>
                                    <th className="p-4 text-xs font-semibold text-slate-600">휴일 근무 횟수</th>
                                    <th className="p-4 text-xs font-semibold text-slate-600">지각 횟수</th>
                                    <th className="p-4 text-xs font-semibold text-slate-600">지각</th>
                                    <th className="p-4 text-xs font-semibold text-slate-600">휴가 사용</th>
                                    <th className="p-4 text-xs font-semibold text-slate-600">대체휴무 사용</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {[...stats].reverse().map((row) => (
                                        <tr key={row.month} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="p-4 text-sm font-bold text-slate-800">{row.month}</td>
                                            <td className="p-4 text-sm text-slate-600">{formatDecimalToHours(row.avg_hours)}</td>
                                            <td className="p-4 text-sm text-slate-600">{formatDecimalToHours(row.night_hours || 0)}</td>
                                            <td className="p-4 text-sm text-slate-600">{row.night_count || 0}회</td>
                                            <td className="p-4 text-sm text-slate-600">{formatDecimalToHours(row.holiday_hours || 0)}</td>
                                            <td className="p-4 text-sm text-slate-600">{row.holiday_count || 0}회</td>
                                            <td className="p-4 text-sm text-rose-600 font-bold">{row.late_count || 0}회</td>
                                            <td className="p-4 text-sm text-slate-600">{formatMinutes(row.late_minutes || 0)}</td>
                                            <td className="p-4 text-sm text-blue-600 font-bold">{row.vacation_days || 0}일</td>
                                            <td className="p-4 text-sm text-blue-600 font-bold">{formatDecimalToHours(row.comp_time_hours || 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : !isLoading && (
                    <div className="text-center py-10 text-slate-400 text-sm">
                        {selectedEmployee ? "조회된 통계 데이터가 없습니다." : ""}
                    </div>
                )}
            </div>
            {isLoading && (
                <Spinner/>
            )}

            {/* 모바일용 카드 리스트 (데이터 배열 순회) */}
            <div className="md:hidden space-y-4">
                {!isLoading && stats.length > 0 ? (
                    <>
                        {[...stats].reverse().map((row) => (
                            <div key={row.month} className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
                                <div className="flex justify-between items-center mb-4 border-b pb-3">
                                    <h3 className="font-bold text-lg text-slate-800">{row.month}</h3>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3">
                                    {/* 주요 지표 강조 (가로 2열) */}
                                    <div className="col-span-2 grid grid-cols-2 gap-3">
                                        <div className="bg-blue-50 p-3 rounded-xl">
                                            <div className="text-[10px] text-blue-500">주간평균 근무</div>
                                            <div className="font-bold text-slate-700">{formatDecimalToHours(row.avg_hours)}</div>
                                        </div>
                                        <div className="bg-white p-3 rounded-xl">

                                        </div>
                                    </div>
                                    
                                    {/* 상세 지표 (6개 항목) */}
                                    <div className="bg-slate-50 p-3 rounded-xl">
                                        <div className="text-[10px] text-slate-500">야간 근무</div>
                                        <div className="font-bold text-slate-700">{formatDecimalToHours(row.night_hours || 0)}</div>
                                    </div>
                                    <div className="bg-slate-50 p-3 rounded-xl">
                                        <div className="text-[10px] text-slate-500">야간 근무 횟수</div>
                                        <div className="font-bold text-slate-700">{row.night_count || 0}회</div>
                                    </div>
                                    <div className="bg-slate-50 p-3 rounded-xl">
                                        <div className="text-[10px] text-slate-500">휴일 근무</div>
                                        <div className="font-bold text-slate-700">{formatDecimalToHours(row.holiday_hours || 0)}</div>
                                    </div>
                                    <div className="bg-slate-50 p-3 rounded-xl">
                                        <div className="text-[10px] text-slate-500">휴일 근무 횟수</div>
                                        <div className="font-bold text-slate-700">{row.holiday_count || 0}회</div>
                                    </div>
                                    {/* 지각 관련 항목 (강조) */}
                                    <div className="bg-rose-50 p-3 rounded-xl">
                                        <div className="text-[10px] text-rose-500">지각 횟수</div>
                                        <div className="font-bold text-rose-700">{row.late_count || 0}회</div>
                                    </div>
                                    <div className="bg-rose-50 p-3 rounded-xl">
                                        <div className="text-[10px] text-rose-500">지각</div>
                                        <div className="font-bold text-rose-700">{formatMinutes(row.late_minutes) || 0}</div>
                                    </div>
                                    <div className="bg-blue-50 p-3 rounded-xl">
                                        <div className="text-[10px] text-blue-500">휴가 사용</div>
                                        <div className="font-bold text-slate-700">{row.vacation_days || 0}일</div>
                                    </div>
                                    <div className="bg-blue-50 p-3 rounded-xl">
                                        <div className="text-[10px] text-blue-500">대체휴무 사용</div>
                                        <div className="font-bold text-blue-700">{formatDecimalToHours(row.comp_time_hours || 0)}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </>
                ) : !isLoading && (
                    <div className="text-center py-10 text-slate-400 text-sm">
                        {selectedEmployee ? "조회된 통계 데이터가 없습니다." : ""}
                    </div>
                )}
            </div>
        </>
    );
};

export default PersonalStats;